import {
  DndContext,
  closestCenter,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import {HStack, Icon} from 'platform/foundation';
import styled from 'styled-components';

import {useState} from 'react';

import {isNil, sortBy} from 'ramda';

import {useSourcingGetPresetsListQuery, useSourcingReorderPresetsMutation} from '@dms/api/core';
import {testIds} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {useSourcingCurrency} from '../../../../hooks/useSourcingCurrency';
import {useSourcingTargetCountry} from '../../../../hooks/useSourcingTargetCountry';
import {SourcingType, useSourcingType} from '../../../../hooks/useSourcingType';
import {SourcingFilterPresetsControl} from '../../hooks/useSourcingFilterPresets';
import {FilterPresetButton} from './FilterPresetButton';
import {SortableFilterPresetButton} from './SortableFilterPresetButton';

interface SourcingFilterPresetButtonsProps {
  filterPresetsControl: SourcingFilterPresetsControl;
  isSortingEnabled?: boolean;
}

export function FilterPresetButtons(props: SourcingFilterPresetButtonsProps) {
  const targetCountry = useSourcingTargetCountry();
  const currency = useSourcingCurrency();
  const sourcingType = useSourcingType();
  const {data} = useSourcingGetPresetsListQuery({
    country: targetCountry?.code,
    currency: currency?.code,
  });
  const [reorderPresets] = useSourcingReorderPresetsMutation();
  const [draggedId, setDraggedId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const orderedPresets = sortBy((a) => a.position, data ?? []);
  const presetIds = orderedPresets.map((preset) => preset.id);

  const onDragStart = (event: DragStartEvent) => {
    setDraggedId(event.active.id.toString());
  };

  const onReorder = (event: DragEndEvent) => {
    const {active, over} = event;

    if (isNil(over) || active.id === over.id) {
      setDraggedId(null);
      return;
    }

    const oldIndex = orderedPresets.findIndex((preset) => preset.id === active.id);
    const newIndex = orderedPresets.findIndex((preset) => preset.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const reorderedFilterPresets = arrayMove(orderedPresets, oldIndex, newIndex);

      return reorderPresets({
        reorderPresetsRequestBody: {
          items: reorderedFilterPresets.map((reorderedFilterPreset, index) => ({
            id: reorderedFilterPreset.id,
            position: index + 1,
          })),
        },
      })
        .unwrap()
        .catch(handleApiError)
        .finally(() => setDraggedId(null));
    }

    setDraggedId(null);
  };

  if (props.isSortingEnabled) {
    const draggedPreset = draggedId
      ? orderedPresets.find((preset) => preset.id === draggedId)
      : null;

    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={onDragStart}
        onDragEnd={onReorder}
      >
        <SortableContext items={presetIds} strategy={horizontalListSortingStrategy}>
          <HStack grow={1} spacing={2} wrap>
            {orderedPresets.map((filterPreset) => (
              <SortableFilterPresetButton
                key={filterPreset.id}
                id={filterPreset.id}
                title={filterPreset.name}
                count={sourcingType === SourcingType.AUCTIONS ? null : filterPreset.newCount}
                onClick={() => props.filterPresetsControl.onSelectFilterPresetId(filterPreset.id)}
                isSelected={filterPreset.id === props.filterPresetsControl.selectedFilterPresetId}
                isNotificationsMuted={
                  sourcingType === SourcingType.AUCTIONS ? null : !filterPreset.allowNotifications
                }
                onResetFilterPreset={props.filterPresetsControl.onResetFilterPreset}
              />
            ))}
          </HStack>
        </SortableContext>
        <DragOverlay>
          {draggedPreset ? (
            <DragOverlayWrapper>
              <DragHandle>
                <Icon
                  value="action/drag_indicator"
                  color="palettes.neutral.60.100"
                  data-testid={testIds.sourcing.classifieds('filterPresetButtons-drag')}
                />
              </DragHandle>
              <FilterPresetButton
                id={draggedPreset.id}
                title={draggedPreset.name}
                count={sourcingType === SourcingType.AUCTIONS ? null : draggedPreset.newCount}
                isSelected={draggedPreset.id === props.filterPresetsControl.selectedFilterPresetId}
                isNotificationsMuted={
                  sourcingType === SourcingType.AUCTIONS ? null : !draggedPreset.allowNotifications
                }
              />
            </DragOverlayWrapper>
          ) : null}
        </DragOverlay>
      </DndContext>
    );
  }

  return (
    <HStack grow={1} spacing={2} wrap>
      {orderedPresets.map((filterPreset) => (
        <FilterPresetButton
          key={filterPreset.id}
          id={filterPreset.id}
          title={filterPreset.name}
          count={sourcingType === SourcingType.AUCTIONS ? null : filterPreset.newCount}
          isSimple
          onClick={() => props.filterPresetsControl.onSelectFilterPresetId(filterPreset.id)}
          isSelected={filterPreset.id === props.filterPresetsControl.selectedFilterPresetId}
          isNotificationsMuted={
            sourcingType === SourcingType.AUCTIONS ? null : !filterPreset.allowNotifications
          }
          onResetFilterPreset={props.filterPresetsControl.onResetFilterPreset}
        />
      ))}
    </HStack>
  );
}

const DragOverlayWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const DragHandle = styled.div`
  cursor: grabbing;
  display: flex;
  align-items: center;
`;
